/**
 * 屏幕 2：异化结果详情页
 *
 * 设计（最新）：
 *  - 左：人偶立绘 + 名字 + 等级标签 + buff UI
 *  - 右（自上而下）：
 *      1. 综合品级卡（品级大字 + 分数）
 *      2. 品级原因卡（五行 + 德性 + 音阶 + 三才 + 纳音）
 *      3. 五行修改建议（基于《五行大义》补缺原则）
 *      4. 两个按钮：五行阶级金字塔 / 换名字
 *  - 完全像素风，无怪兽系内容
 */

import { createSnow } from "../snow";
import { drawFigure } from "../figureSprite";
import { RANK_LABELS, type RankLevel } from "../ranking";
import { buildNameAdvice } from "../nameAdvisor";
import type { CharAnalysis, FameBuff, Element } from "../../shared/types";

export interface Screen2Deps {
  root: HTMLElement;
  fontFamily: string;
  analysis: CharAnalysis;
  zooResult?: { added: boolean; reason?: "duplicate" };
  onRegenerate: () => void;
  onBack: () => void;
  onGoZoo?: () => void;
}

export interface Screen2Controller {
  destroy: () => void;
}

/** 稳定哈希 */
function hashStr(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** fortune 文本 → 数字（1-5） */
function fortuneToLevel(rank: string): RankLevel {
  return (
    { "上品": 1, "中上品": 2, "中品": 3, "中下品": 4, "下品": 5 } as Record<string, RankLevel>
  )[rank] ?? 5;
}

export function createScreen2(deps: Screen2Deps): Screen2Controller {
  const { root, analysis, zooResult, onBack, onGoZoo } = deps;

  const destroySnow = createSnow(root);

  const screen = document.createElement("div");
  screen.className = "screen-2-result";
  root.appendChild(screen);

  // 标题
  const title = document.createElement("div");
  title.className = "title";
  title.textContent = `「${analysis.raw}」`;
  screen.appendChild(title);

  const sub = document.createElement("div");
  sub.className = "subtitle";
  sub.textContent = "FIVE ELEMENTS · GRADING";
  screen.appendChild(sub);

  // 同名重复提示
  if (zooResult && !zooResult.added && zooResult.reason === "duplicate") {
    const dup = document.createElement("div");
    dup.className = "dup-hint";
    dup.innerHTML = `※ 名称重复：<b>「${analysis.raw}」</b> 已存在图鉴中（同名不入园，五行与分数固定不变）`;
    screen.appendChild(dup);
  }

  // 主容器：左人偶 + 右信息
  const main = document.createElement("div");
  main.className = "result-main";

  // ========== 左：人偶 ==========
  const left = document.createElement("div");
  left.className = "result-left";

  const figureBox = document.createElement("div");
  figureBox.className = "figure-box";

  // 大画布：160×160 显示 32×32 人偶（5x 放大）
  const canvas = document.createElement("canvas");
  canvas.width = 32;
  canvas.height = 32;
  canvas.className = "figure-canvas-big";
  figureBox.appendChild(canvas);

  // 确定元素和等级
  const el = (analysis.dominantElement ?? "土") as Element;
  const baseLevel: RankLevel = analysis.fortune
    ? fortuneToLevel(analysis.fortune.rank)
    : 3;
  const fame: FameBuff | undefined = analysis.fame;
  const effectiveLevel: RankLevel = fame?.hit
    ? (fame.effectiveRank === "一等" ? 1 : fame.effectiveRank === "二等" ? 2 : fame.effectiveRank === "三等" ? 3 : fame.effectiveRank === "四等" ? 4 : 5)
    : baseLevel;

  drawFigure(canvas, el, effectiveLevel, hashStr(analysis.raw));

  if (fame?.hit) {
    const badge = document.createElement("div");
    badge.className = "buff-badge";
    badge.innerHTML = `<span class="buff-char">名</span>`;
    badge.title = `【${fame.title}】${fame.displayName}\n加成：+${fame.scoreBonus}分`;
    figureBox.appendChild(badge);
  }

  const figName = document.createElement("div");
  figName.className = "figure-name-big";
  figName.textContent = analysis.raw;
  figureBox.appendChild(figName);

  const figRank = document.createElement("div");
  figRank.className = "figure-rank-big";
  figRank.textContent = RANK_LABELS[effectiveLevel];
  figRank.style.color = tierColor(effectiveLevel);
  figureBox.appendChild(figRank);

  left.appendChild(figureBox);

  const elTag = document.createElement("div");
  elTag.className = `element-tag element-${el}`;
  elTag.innerHTML = `<span class="et-name">${el}</span><span class="et-trait">${elementTrait(el)}</span>`;
  left.appendChild(elTag);

  main.appendChild(left);

  // ========== 右：品级信息 ==========
  const right = document.createElement("div");
  right.className = "result-right";

  // 1. 品级大数字
  const rankCard = document.createElement("div");
  rankCard.className = "rank-card";
  rankCard.innerHTML = `
    <div class="rc-label">综合品级</div>
    <div class="rc-rank" style="color:${tierColor(effectiveLevel)}">${RANK_LABELS[effectiveLevel]}</div>
    <div class="rc-score">分：<span style="color:${tierColor(effectiveLevel)}">${fame?.hit ? fame.effectiveTotal : analysis.fortune?.total ?? 0}</span>${fame?.hit && fame.scoreBonus > 0 ? `<span class="rc-bonus">(+${fame.scoreBonus})</span>` : ""}</div>
  `;
  right.appendChild(rankCard);

  // 2. 品级原因
  const reasonCard = document.createElement("div");
  reasonCard.className = "reason-card";
  const fortune = analysis.fortune;
  let reasonHTML = `<div class="rc-title">◆ 品级原因</div>`;
  if (fortune) {
    reasonHTML += `<div class="rc-line"><span class="rc-key">总评</span><span class="rc-val">${fortune.verse}</span></div>`;
    reasonHTML += `<div class="rc-line"><span class="rc-key">五行</span><span class="rc-val">${el}（主导）</span></div>`;
    reasonHTML += `<div class="rc-line"><span class="rc-key">德性</span><span class="rc-val">${fortune.breakdown.virtue.note}</span></div>`;
    reasonHTML += `<div class="rc-line"><span class="rc-key">音阶</span><span class="rc-val">${fortune.breakdown.pitch.note}</span></div>`;
    reasonHTML += `<div class="rc-line"><span class="rc-key">三才</span><span class="rc-val">${fortune.breakdown.sanCai.note}</span></div>`;
    reasonHTML += `<div class="rc-line"><span class="rc-key">纳音</span><span class="rc-val">${fortune.nayin}</span></div>`;
  }
  reasonCard.innerHTML = reasonHTML;
  right.appendChild(reasonCard);

  // 3. 名人 buff（仅命中时）
  if (fame?.hit) {
    const fameCard = document.createElement("div");
    fameCard.className = "fame-card";
    const tierLabel = fame.tier === "world" ? "世界级" : fame.tier === "continent" ? "洲级别" : fame.tier === "east-asia" ? "东亚级" : fame.tier === "nation" ? "国家级" : "地区级";
    const hs = fame.historyScore ?? 0;
    const sb = fame.scoreBonus;
    const buffChips: string[] = [];
    if (sb > 0) {
      buffChips.push(`<span class="fame-buff">⬆ 综合分 +${sb}</span>`);
    } else if (sb < 0) {
      buffChips.push(`<span class="fame-buff negative">⬇ 综合分 ${sb}</span>`);
    }
    if (hs > 0) {
      buffChips.push(`<span class="fame-buff hist-pos">✦ 贡献 +${hs}</span>`);
    } else if (hs < 0) {
      buffChips.push(`<span class="fame-buff hist-neg">☠ 遗臭 -${Math.abs(hs)}</span>`);
    }
    fameCard.innerHTML = `
      <div class="rc-title">◆ 名人增益</div>
      <div class="fame-line"><span class="fame-name">${fame.displayName}</span><span class="fame-title">${fame.title}</span></div>
      <div class="fame-meta">${fame.field} · ${fame.era} · ${tierLabel}</div>
      ${fame.contribution ? `<div class="fame-contribution">${fame.contribution}</div>` : ""}
        <div class="fame-buffs">
          ${buffChips.join(" ")}
        </div>
    `;
    right.appendChild(fameCard);
  }

  // 4. 五行修改建议（基于《五行大义》补缺）
  const adviceCard = document.createElement("div");
  adviceCard.className = "advice-card";
  const missing = analysis.missingElements ?? [];
  const advice = buildNameAdvice(missing, el);
  let adviceHTML = `<div class="rc-title">${advice.title}</div>`;
  adviceHTML += `<blockquote class="advice-quote"><span class="aq-text">${advice.quote}</span><span class="aq-src">${advice.quoteSource}</span></blockquote>`;

  if (advice.suggestions.length > 0) {
    adviceHTML += `<div class="advice-missing">缺失：${missing.map((m) => `<span class="element-badge-mc element-${m}">${m}</span>`).join(" ")}</div>`;
    adviceHTML += `<div class="advice-list">`;
    for (const s of advice.suggestions) {
      adviceHTML += `
        <div class="advice-row">
          <div class="advice-head">
            <span class="advice-el element-${s.element}">补 ${s.element}</span>
            <span class="advice-radical">字根：${s.radical}</span>
          </div>
          <div class="advice-reason">${s.reason}</div>
          <div class="advice-examples">例字：${s.examples.map((e) => `<span class="advice-ex">${e}</span>`).join(" ")}</div>
        </div>
      `;
    }
    adviceHTML += `</div>`;
  } else {
    adviceHTML += `<div class="advice-all">五行俱全，气运周全，无需大改。</div>`;
  }

  adviceHTML += `<div class="advice-general">`;
  for (const tip of advice.generalTips) {
    adviceHTML += `<div class="advice-tip">· ${tip}</div>`;
  }
  adviceHTML += `</div>`;

  adviceCard.innerHTML = adviceHTML;
  right.appendChild(adviceCard);

  // 5. 按钮区（紧跟在原因和建议下方，与上方保持间距）
  const actions = document.createElement("div");
  actions.className = "result-actions";

  const pyramidBtn = document.createElement("button");
  pyramidBtn.className = "primary pyramid-btn";
  pyramidBtn.innerHTML = `<svg viewBox="0 0 16 16" fill="currentColor"><rect x="6" y="2" width="4" height="2"/><rect x="4" y="4" width="8" height="2"/><rect x="2" y="6" width="12" height="2"/><rect x="0" y="8" width="16" height="2"/></svg><span>五行阶级金字塔</span>`;
  pyramidBtn.addEventListener("click", () => onGoZoo && onGoZoo());
  actions.appendChild(pyramidBtn);

  const backBtn = document.createElement("button");
  backBtn.className = "nav-btn";
  backBtn.textContent = "换名字";
  backBtn.addEventListener("click", () => onBack());
  actions.appendChild(backBtn);

  right.appendChild(actions);

  main.appendChild(right);
  screen.appendChild(main);

  return {
    destroy: () => {
      destroySnow();
      if (screen.parentNode) screen.parentNode.removeChild(screen);
    },
  };
}

function tierColor(level: RankLevel): string {
  switch (level) {
    case 1: return "#ffe680";
    case 2: return "#e0e0e8";
    case 3: return "#c89868";
    case 4: return "#9a8a7a";
    case 5: return "#6a5a4a";
  }
}

function elementTrait(el: Element): string {
  return (
    { 金: "肃杀成器", 木: "生发向上", 水: "润下深邃", 火: "炎上炽烈", 土: "稼穑承载" } as const
  )[el];
}